package com.playoffpulse.frontend.data

import com.playoffpulse.shared.model.LeagueId
import com.playoffpulse.shared.model.BestCaseClinchResponse
import com.playoffpulse.shared.model.BlockedShotsChartBundleResponse
import com.playoffpulse.shared.model.BlockedShotsMissesMode
import com.playoffpulse.shared.model.BlockedShotsChartResponse
import com.playoffpulse.shared.model.BlockedShotsTeamDetailResponse
import com.playoffpulse.shared.model.DraftWatchResponse
import com.playoffpulse.shared.model.ImportantGamesResponse
import com.playoffpulse.shared.model.ImportantGamesObjective
import com.playoffpulse.shared.model.ImportantGamesWindow
import com.playoffpulse.shared.model.MagicSummary
import com.playoffpulse.shared.model.StandingsInsightsResponse
import com.playoffpulse.shared.model.StandingsSnapshot
import com.playoffpulse.shared.model.TeamRef
import com.playoffpulse.shared.model.TopRivalsResponse
import com.playoffpulse.shared.model.VanquishedResponse
import com.playoffpulse.shared.model.YesterdayMovementResponse
import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.serialization.builtins.ListSerializer
import kotlinx.serialization.json.Json

class BackendApiProvider(
    private val baseUrl: String = "/api/v1",
    private val json: Json = Json { ignoreUnknownKeys = true },
) : PlayoffDataProvider {
    private val normalizedBaseUrl: String = baseUrl.trimEnd('/')

    override suspend fun getTeams(league: LeagueId): List<TeamRef> {
        val responseText = fetchText("$normalizedBaseUrl/teams?league=${league.name}")
        return json.decodeFromString(ListSerializer(TeamRef.serializer()), responseText)
    }

    override suspend fun getStandings(league: LeagueId): StandingsSnapshot {
        val responseText = fetchText("$normalizedBaseUrl/standings?league=${league.name}")
        return json.decodeFromString(StandingsSnapshot.serializer(), responseText)
    }

    override suspend fun getStandingsInsights(league: LeagueId): StandingsInsightsResponse {
        val responseText = fetchText("$normalizedBaseUrl/standings-insights?league=${league.name}")
        return json.decodeFromString(StandingsInsightsResponse.serializer(), responseText)
    }

    override suspend fun getBlockedShotsChart(
        league: LeagueId,
        missesMode: BlockedShotsMissesMode,
    ): BlockedShotsChartResponse {
        val missesQuery = if (missesMode == BlockedShotsMissesMode.INCLUDE) "" else "&misses=exclude"
        val responseText = fetchText("$normalizedBaseUrl/charts/blocked-shots?league=${league.name}$missesQuery")
        return json.decodeFromString(BlockedShotsChartResponse.serializer(), responseText)
    }

    override suspend fun getBlockedShotsChartBundle(
        league: LeagueId,
        missesMode: BlockedShotsMissesMode,
    ): BlockedShotsChartBundleResponse {
        val missesQuery = if (missesMode == BlockedShotsMissesMode.INCLUDE) "" else "&misses=exclude"
        val responseText = fetchText("$normalizedBaseUrl/charts/blocked-shots/bundle?league=${league.name}$missesQuery")
        return json.decodeFromString(BlockedShotsChartBundleResponse.serializer(), responseText)
    }

    override suspend fun getBlockedShotsTeamDetail(
        teamId: String,
        league: LeagueId,
        missesMode: BlockedShotsMissesMode,
    ): BlockedShotsTeamDetailResponse {
        val missesQuery = if (missesMode == BlockedShotsMissesMode.INCLUDE) "" else "&misses=exclude"
        val responseText = fetchText(
            "$normalizedBaseUrl/charts/blocked-shots/team/${teamId.uppercase()}?league=${league.name}$missesQuery",
        )
        return json.decodeFromString(BlockedShotsTeamDetailResponse.serializer(), responseText)
    }

    override suspend fun getMagicSummary(teamId: String, league: LeagueId): MagicSummary {
        val responseText = fetchText("$normalizedBaseUrl/magic-summary/${teamId.uppercase()}?league=${league.name}")
        return json.decodeFromString(MagicSummary.serializer(), responseText)
    }

    override suspend fun getImportantGames(
        teamId: String,
        window: ImportantGamesWindow,
        objective: ImportantGamesObjective,
        league: LeagueId,
    ): ImportantGamesResponse {
        val responseText = fetchText(
            "$normalizedBaseUrl/important-games?league=${league.name}&teamId=${teamId.uppercase()}&window=${window.name.lowercase()}&objective=${objective.name.lowercase()}",
        )
        return json.decodeFromString(ImportantGamesResponse.serializer(), responseText)
    }

    override suspend fun getTopRivals(teamId: String, league: LeagueId): TopRivalsResponse {
        val responseText = fetchText("$normalizedBaseUrl/top-rivals?league=${league.name}&teamId=${teamId.uppercase()}")
        return json.decodeFromString(TopRivalsResponse.serializer(), responseText)
    }

    override suspend fun getBestCaseClinch(teamId: String, league: LeagueId): BestCaseClinchResponse {
        val responseText = fetchText("$normalizedBaseUrl/best-case-clinch/${teamId.uppercase()}?league=${league.name}")
        return json.decodeFromString(BestCaseClinchResponse.serializer(), responseText)
    }

    override suspend fun getVanquished(teamId: String, league: LeagueId): VanquishedResponse {
        val responseText = fetchText("$normalizedBaseUrl/vanquished?league=${league.name}&teamId=${teamId.uppercase()}")
        return json.decodeFromString(VanquishedResponse.serializer(), responseText)
    }

    override suspend fun getDraftWatch(teamId: String, league: LeagueId): DraftWatchResponse {
        val responseText = fetchText("$normalizedBaseUrl/draft-watch?league=${league.name}&teamId=${teamId.uppercase()}")
        return json.decodeFromString(DraftWatchResponse.serializer(), responseText)
    }

    override suspend fun getYesterdayMovement(league: LeagueId): YesterdayMovementResponse {
        val responseText = fetchText("$normalizedBaseUrl/yesterday-movement?league=${league.name}")
        return json.decodeFromString(YesterdayMovementResponse.serializer(), responseText)
    }

    private suspend fun fetchText(url: String): String {
        val response = window.fetch(url).await()
        if (!response.ok) {
            throw ProviderException("Backend API request failed (${response.status})")
        }
        return response.text().await()
    }
}
